import { useState, useEffect } from 'react';
import { ListItemIcon, ListItemText, List, Grid, Typography, Paper, ListItemButton } from '@mui/material';
import { TextFieldsRounded, SubjectRounded, ImageRounded, AssessmentRounded } from '@mui/icons-material';
import { FlashOnRounded, AdjustRounded, ViewStreamRounded, WorkOutlineRounded } from '@mui/icons-material';
import { ChevronRightRounded, Timeline, FolderRounded } from '@mui/icons-material';
import { SelectFolderDialog } from '../../../../Global/Components';
import { grey, blue } from '@mui/material/colors';
import AddKpiDialog from '../../../kpiHub/components/AddKpiDialog';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { cloneDeep, isArray } from 'lodash';
import AddActionDialog from './AddActionDialog';
import AddLinkDialog from './AddLinkDialog';
import TaskBoxOptions from './TaskBoxOptions';
import ProcessTableDialog from './ProcessTableDialog';
import { v4 } from 'uuid';
import DashboardItem from './dashboard/DashboardItem';
const ResponsiveReactGridLayout = WidthProvider(Responsive);

const itemTypes = [
  {
    icon: <TextFieldsRounded style={{ color: grey[500] }} />,
    text: 'Heading',
    type: 'heading',
  },
  {
    icon: <SubjectRounded style={{ color: grey[500] }} />,
    text: 'Text box',
    type: 'text',
  },
  {
    icon: <ImageRounded style={{ color: grey[500] }} />,
    text: 'Image box',
    type: 'image',
  },
  {
    icon: <AssessmentRounded style={{ color: grey[500] }} />,
    text: 'KPI',
    type: 'kpi',
  },
  {
    icon: <FlashOnRounded style={{ color: grey[500] }} />,
    text: 'Action',
    type: 'action',
  },
  {
    icon: <AdjustRounded style={{ color: grey[500] }} />,
    text: 'Shortcut',
    type: 'shortcut',
  },
  {
    icon: <ViewStreamRounded style={{ color: grey[500] }} />,
    text: 'Shortcut bar',
    type: 'shortcutBar',
  },
  {
    icon: <WorkOutlineRounded style={{ color: grey[500] }} />,
    text: 'Task box',
    type: 'taskBox',
  },
  {
    icon: <Timeline style={{ color: grey[500] }} />,
    text: 'Process table',
    type: 'processTable',
  },
  {
    icon: <FolderRounded style={{ color: grey[500] }} />,
    text: 'Document folder',
    type: 'dlfolder',
  },
];

export default function Dashboard({ initialItems, initialLayouts, editable, onChange, forPortal, portalSpaceId }) {
  const [layouts, setLayouts] = useState({});
  const [items, setItems] = useState([]);
  const [addKpiDialog, setAddKpiDialog] = useState(false);
  const [addActionDialog, setAddActionDialog] = useState(false);
  const [addLinkDialog, setAddLinkDialog] = useState(false);
  const [taskBoxDialog, setTaskBoxDialog] = useState(false);
  const [processTableDialog, setProcessTableDialog] = useState(false);
  const [dlFolderDialog, setDlFolderDialog] = useState(false);

  useEffect(() => {
    if (!Array.isArray(initialItems)) return;
    setItems(cloneDeep(initialItems));
  }, [initialItems]);

  useEffect(() => {
    // to resize layout
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
  }, [editable]);

  useEffect(() => {
    if (!initialLayouts) return;
    setLayouts({ ...initialLayouts });
  }, [initialLayouts]);

  function addItem(item, type) {
    let res;
    if (type === 'kpi') {
      res = { data: item, id: v4(), type };
    }
    if (res) {
      items.push(res);
      setItems([...items]);
      onChange({ items, layouts });
    }
  }

  function saveItem(index, data) {
    if (items[index]?.type === 'taskBox' && items[index]?.data?.displayAs !== data?.displayAs) {
      items.push({ data, id: v4(), type: 'taskBox' });
      setItems(items.filter((x) => x.id !== items[index].id));
    } else if (items[index]?.type === 'processTable') {
      items[index] = { ...items[index], ...data };
    } else {
      items[index].data = data;
    }

    onChange({ items, layouts });
  }

  function onItemAdd(type) {
    if (type === 'kpi') {
      setAddKpiDialog(true);
    } else if (type === 'heading' || type === 'text') {
      items.push({ data: { text: '' }, id: v4(), type });
      setItems([...items]);
      onChange({ items, layouts });
    } else if (type === 'image') {
      items.push({ data: { file: null }, id: v4(), type });
      setItems([...items]);
      onChange({ items, layouts });
    } else if (type === 'action') {
      setAddActionDialog(true);
    } else if (type === 'shortcut') {
      setAddLinkDialog(true);
    } else if (type === 'shortcutBar') {
      items.push({ data: { title: '', items: [] }, id: v4(), type });
      setItems([...items]);
    } else if (type === 'taskBox') {
      setTaskBoxDialog(true);
    } else if (type === 'processTable') {
      setProcessTableDialog(true);
    } else if (type === 'dlfolder') {
      setDlFolderDialog(true);
    }
  }

  function addShortcut(shortcut) {
    items.push({ data: shortcut, id: v4(), type: 'shortcut' });
    setItems([...items]);
    onChange({ items, layouts });
  }

  function addAction(action) {
    items.push({ data: action, id: v4(), type: 'action' });
    setItems([...items]);
    onChange({ items, layouts });
  }

  function deleteItem(id) {
    setItems(items.filter((x) => x.id !== id));
  }

  function addTaskBox(options) {
    items.push({ data: options, id: v4(), type: 'taskBox' });
    setItems([...items]);
    onChange({ items, layouts });
  }

  function editItem(itemId, update, layoutUpdate) {
    const index = items.findIndex((x) => x.id === itemId);
    if (index === -1) return;
    items[index].data = { ...items[index].data, ...update };
    if (layoutUpdate) {
      // otherwise stupid layout does not update
      resizeLayoutItem(itemId, layoutUpdate);
      const itemsCopy = cloneDeep(items);
      const layoutsCopy = cloneDeep(layouts);
      deleteItem(itemId);
      setTimeout(() => {
        setLayouts(layoutsCopy);
        setItems(itemsCopy);
      }, 20);
      onChange({ items: itemsCopy, layouts: layoutsCopy });
    } else {
      onChange({ items, layouts });
    }
  }

  function getMinWidth(item) {
    if (item.type === 'shortcut') return 3; // replace if hight
    if (item.type === 'shortcutBar') return 20;
    if (item.type === 'processTable' && item.view !== 'button') return 18;
    if (item.type === 'taskBox' && item.data?.displayAs === 'table') return 20;
    return 3;
  }

  function getDefaultWidth(item) {
    if (item.type === 'heading') return 20;
    if (item.type === 'shortcutBar') return 20;
    if (item.type === 'processTable' && item.view !== 'button') return 18;
    if (item.type === 'taskBox' && item.data?.displayAs === 'table') return 20;
    return 6;
  }

  function getMinHeight(item) {
    if (item.type === 'shortcutBar') return 6;
    if (item.type === 'processTable' && item.view !== 'button') return 8;
    if (item.type === 'taskBox' && item.data?.displayAs === 'table') return 8;
    return 2;
  }

  function getDefaultHeight(item) {
    if (item.type === 'heading') return 5;
    if (item.type === 'action') return 2;
    if (item.type === 'kpi') return 10;
    return 8;
  }

  function saveProcessTable(data) {
    items.push({ data: data.processId, id: v4(), type: 'processTable', ...data });
    setItems([...items]);
    onChange({ items, layouts });
  }

  function addDlFolder(folder) {
    items.push({ data: { folder }, id: v4(), type: 'dlfolder' });
    setItems([...items]);
    onChange({ items, layouts });
  }

  function resizeLayoutItem(itemId, layoutUpdate) {
    const keys = Object.keys(layouts);
    for (let i = 0; i < keys.length; ++i) {
      const config = layouts[keys[i]];
      if (!isArray(config)) continue;
      const itemIndex = config.findIndex((x) => x.i === itemId);
      if (itemIndex === -1) continue;

      config[itemIndex] = { ...config[itemIndex], ...layoutUpdate };
    }
  }

  return (
    <Grid container wrap="nowrap" alignItems="flex-start" style={{ overflow: 'hidden', padding: '0px 0' }}>
      <Grid container item xs={editable ? 10 : 12}>
        {items.length === 0 && (
          <Grid container alignItems="center" justifyContent="center" style={{ height: '25vh' }}>
            <Typography variant="h6">
              Dashboard is empty <span style={{ fontSize: '2em' }}>😐</span>
            </Typography>
          </Grid>
        )}
        <ResponsiveReactGridLayout
          className="layout"
          rowHeight={20}
          cols={{ lg: 24, md: 20, sm: 10, xs: 4, xxs: 4 }}
          breakpoints={{ lg: 800, md: 600, sm: 400, xs: 300, xxs: 0 }}
          onLayoutChange={(curr, all) => {
            if (!editable) return;
            const res = { lg: curr, md: curr, sm: curr, xs: curr, xxs: curr };
            setLayouts(res);
            onChange({ items, layouts: res });
          }}
          useCSSTransforms
          style={{ width: '100%' }}
          margin={[10, 10]}
          layouts={layouts}
          isDraggable={Boolean(editable)}
          isResizable={Boolean(editable)}
          draggableCancel=".non-draggable"
        >
          {items.map((item, index) => (
            <div
              key={item.id}
              data-grid={{
                x: index * 4,
                y: 0,
                w: getDefaultWidth(item),
                h: getDefaultHeight(item),
                i: item.id,
                minW: getMinWidth(item),
                minH: getMinHeight(item),
              }}
            >
              <DashboardItem
                item={item}
                onChange={editItem}
                onSave={(data) => saveItem(index, data)}
                onDelete={deleteItem}
                portalSpaceId={portalSpaceId}
                forPortal={forPortal}
                editable={editable}
              />
              {editable && (
                <div className="react-resizable-handle">
                  <ChevronRightRounded fontSize="large" style={{ color: grey[300], transform: 'rotate(45deg)' }} />
                </div>
              )}
            </div>
          ))}
        </ResponsiveReactGridLayout>
      </Grid>
      {editable && (
        <Grid container item xs={2} style={{ paddingLeft: 24 }}>
          <Paper
            variant="outlined"
            style={{ border: `2px solid ${blue[500]}`, borderRadius: 12, padding: '1rem 0px', width: '100%' }}
          >
            <Grid container>
              <Typography variant="h6" gutterBottom style={{ marginLeft: '0.75rem' }}>
                Add to dashboard
              </Typography>
              <List style={{ width: '100%' }}>
                {itemTypes.map((item, i) => (
                  <ListItemButton key={i} onClick={() => onItemAdd(item.type)}>
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                ))}
              </List>
            </Grid>
          </Paper>
          <AddKpiDialog
            open={addKpiDialog}
            onClose={() => setAddKpiDialog(false)}
            onResult={(res) => addItem(res, 'kpi')}
          />
          <AddActionDialog
            forPortal={forPortal}
            open={addActionDialog}
            onClose={() => setAddActionDialog(false)}
            onResult={addAction}
          />
          <AddLinkDialog
            open={addLinkDialog}
            onClose={() => setAddLinkDialog(false)}
            forDashboard
            onResult={addShortcut}
            forPortal={forPortal}
          />
          <TaskBoxOptions open={taskBoxDialog} onClose={() => setTaskBoxDialog(false)} onResult={addTaskBox} />
          <ProcessTableDialog
            open={processTableDialog}
            onClose={() => setProcessTableDialog(false)}
            onResult={saveProcessTable}
          />
          <SelectFolderDialog
            open={dlFolderDialog}
            onClose={() => setDlFolderDialog(false)}
            onResult={addDlFolder}
            excludePersonal
          />
        </Grid>
      )}
    </Grid>
  );
}
